import withLoadingProps from 'next-dynamic-loading-props';
import dynamic from 'next/dynamic';
import { InputFieldData, InputStateProps } from '../../@types/input-field-data';
import TextInput from './text-input';

interface PhoneNumberInputProps extends InputStateProps, InputFieldData {
  type: 'phone_number';
}

/**
 * Creates a dynamic phone input control. `react-international-phone` is quite bulky
 * as it brings a lot of JS and CSS with it. So we load the extended phone number
 * input dynamically, with a fallback control which is just a TextInput.
 *
 * We use the `{ loading }` option to provide the fallback control, we can't use `React.Suspense`
 * because we are still using React 17 (until @storybook/addon-storyshots supports v18).
 *
 * This is all done *outside* the render method to avoid re-rendering after every keypress.
 * We need to access the props in order to render a good fallback input, so we use the clever
 * `next-dynamic-loading-props` plugin.
 */
const DynamicPhoneNumberInput = withLoadingProps<PhoneNumberInputProps>((useLoadingProps) =>
  dynamic(() => import('./phone-number-input-extended'), {
    loading: function PhoneNumberInputFallback() {
      const props = useLoadingProps();
      return (
        <TextInput
          {...props}
          maxLength={40}
          name={props.name || 'phone_number'}
          text={props.text || 'Phone Number'}
          type="tel"
        />
      );
    },
    ssr: false,
  })
);

function PhoneNumberInput(props: PhoneNumberInputProps) {
  return <DynamicPhoneNumberInput {...props} />;
}

export default PhoneNumberInput;
